import React, { useState, useEffect } from 'react';
import { Container, Row, Col, Button, Alert, Card } from 'react-bootstrap';
import { useParams, useNavigate } from 'react-router-dom';
import { QRCodeScanner, ScanResultCard } from '../../components/scanner';
import { apiService } from '../../services/api';
import { ActivityScanResult } from '../../types';
import SkeletonLoader from '../../components/common/SkeletonLoader';
import './ActivityScannerPage.scss';

const ActivityScannerPage: React.FC = () => {
  const { activityPlaceSlug } = useParams<{ activityPlaceSlug: string }>();
  const navigate = useNavigate();
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [placeName, setPlaceName] = useState<string>('');
  const [scanResult, setScanResult] = useState<ActivityScanResult | null>(null);
  const [isScanning, setIsScanning] = useState(true);

  useEffect(() => {
    const fetchPlaceData = async () => {
      if (!activityPlaceSlug) return;

      try {
        setLoading(true);
        const data = await apiService.scanner.getActivityPlaceScannerData(activityPlaceSlug);
        if (data.success && data.activity_place) {
          setPlaceName(data.activity_place.name);
        }
      } catch (err: any) {
        setError(err.message || 'Failed to load activity place data');
      } finally {
        setLoading(false);
      }
    };

    fetchPlaceData();
  }, [activityPlaceSlug]);

  const handleScan = async (decodedText: string) => {
    if (!activityPlaceSlug || !decodedText) return;

    try {
      setIsScanning(false);
      const result = await apiService.scanner.scanActivityBooking(activityPlaceSlug, decodedText);
      setScanResult(result);

      // Resume scanning after a delay
      setTimeout(() => {
        setIsScanning(true);
        setScanResult(null);
      }, 3000);
    } catch (err: any) {
      setError(err.message || 'Failed to scan booking');
      setIsScanning(true);
    }
  };

  const handleScanError = (errorMessage: string) => {
    if (errorMessage && !errorMessage.includes('No QR code found')) {
      console.warn('Scanner error:', errorMessage);
    }
  };

  if (loading) {
    return (
      <Container className="activity-scanner-page py-4">
        <SkeletonLoader />
      </Container>
    );
  }

  return (
    <Container className="activity-scanner-page py-4">
      <div className="page-header mb-4">
        <div className="d-flex justify-content-between align-items-center">
          <div>
            <h2>{placeName || 'Activity Scanner'}</h2>
            <p className="text-muted mb-0">Scan QR codes to check in activity bookings</p>
          </div>
          <div>
            <Button variant="outline-secondary" onClick={() => navigate('/scanner/')}>
              Back to Dashboard
            </Button>
          </div>
        </div>
      </div>

      {error && (
        <Alert variant="danger" dismissible onClose={() => setError(null)}>
          {error}
        </Alert>
      )}

      <Row>
        <Col lg={8}>
          <Card className="scanner-card mb-4">
            <Card.Body>
              <Card.Title>Scanner</Card.Title>
              {isScanning && !scanResult && (
                <QRCodeScanner
                  onScan={handleScan}
                  onError={handleScanError}
                  facingMode="environment"
                />
              )}
              {!isScanning && (
                <div className="scanner-paused text-center py-5">
                  <p className="text-muted">Scanner paused. Processing scan...</p>
                </div>
              )}
            </Card.Body>
          </Card>

          {scanResult && (
            <ScanResultCard result={scanResult} type="activity" />
          )}
        </Col>
      </Row>
    </Container>
  );
};

export default ActivityScannerPage;

